import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRightOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import ContentCard from '@/components/ContentCard';
import LaserFrameHero from '@/components/LaserFrameHero';
import Stakeholders from '@/components/Stakeholders';
import Reveal from '@/components/Reveal';
import { useLang } from '@/hooks/useLang';
import { usePageTitle } from '@/hooks/usePageTitle';
import { pick } from '@/lib/localized';
import type { Localized as L } from '@/lib/localized';
import { formatDate } from '@/lib/format';
import { driveImageUrl } from '@/lib/drive';
import { listEvents, type EventItem } from '@/lib/events';
import { listPress, type PressItem } from '@/lib/press';

const ACCENT = '#3d84ff';

// KUTU KARTLARI — RadarPrograms'taki dönen kutunun yüz tasarımı (pc-card-face):
// üstte başlık, ortada MetallicPaint logo, altta açıklama; marka accent'iyle
// kenar + animasyonlu glow. Destek Programları bölümünde aynen kullanılır.
const SUPPORT_BOX_CARDS: {
  logo: string;
  accent: string;
  title: L;
  desc: L;
  to: string;
}[] = [
  {
    logo: '/terminal-yatay.svg',
    accent: '#fe6a00',
    title: { tr: 'Terminal Çağrı Programı', en: 'Terminal Call Program' },
    desc: {
      tr: 'Yarışma takımlarına atölye, malzeme, prototipleme ve mentorluk desteği sağlıyoruz.',
      en: 'We give competition teams workshop access, materials, prototyping and mentoring support.',
    },
    to: '/program/terminal-call',
  },
  {
    logo: '/eklem-yatay-beyaz.svg',
    accent: '#46c68c',
    title: {
      tr: 'Eklem Topluluk Destek Programı',
      en: 'Eklem Community Support Program',
    },
    desc: {
      tr: 'Toplulukların projelerine teknik ve lojistik destek sağlıyoruz.',
      en: 'We provide technical and logistical support for community projects.',
    },
    to: '/community',
  },
  {
    logo: '/next-level-metal.svg',
    accent: '#fd6a00',
    title: {
      tr: 'Next Level / Sürdürülebilirlik Programı',
      en: 'Next Level / Sustainability Program',
    },
    desc: {
      tr: 'Sürdürülebilirlik odaklı girişimlere ileri seviye destek programı.',
      en: 'An advanced support program for sustainability-focused ventures.',
    },
    to: '/program/next-level',
  },
];

// Gelişim programları — yelpazedeki dörtlünün kutu-kart verisi.
const DEV_BOX_CARDS: {
  logo: string;
  /** Dikey lockup — masaüstündeki uzun (4/5) kartta yatay yerine basılır. */
  logoVertical?: string;
  accent: string;
  title: L;
  desc: L;
  to: string;
}[] = [
  {
    // Marka laciverdine (#272a6e ailesi) uyarlanmış varyant.
    logo: '/rota-metal-lacivert.svg',
    accent: '#6f76d8',
    title: {
      tr: 'Rota Teknoloji Geliştirme',
      en: 'ROTA Technology Development',
    },
    desc: {
      tr: 'Yapay zeka, siber güvenlik ve veri bilimi alanlarında temel geliştirme programı.',
      en: 'A foundational development program in AI, cyber security and data science.',
    },
    to: '/program/rota-tech-development',
  },
  {
    logo: '/tadimlik-atolyeler.svg',
    accent: '#f5a524',
    title: { tr: 'Tadımlık Atölyeler', en: 'Taster Workshops' },
    desc: {
      tr: 'Farklı teknoloji alanlarına giriş niteliğinde kısa süreli atölye çalışmaları.',
      en: 'Short workshops that introduce different areas of technology.',
    },
    to: '/program/tasting-workshops',
  },
  {
    // Koyu zemin varyantı: logonun laciverti (#0C3455) aynı tonun görünür
    // açık hâline (#4e9bda) çekilir, gri amblem korunur. Masaüstündeki uzun
    // kartta dikey lockup, mobildeki kısa kartta yatay.
    logo: '/kapsam-yatay-dark.svg',
    logoVertical: '/kapsam-dikey-dark.svg',
    accent: '#4e9bda',
    title: {
      tr: 'Kapsam Profesyonel Gelişim',
      en: 'Kapsam Professional Development',
    },
    desc: {
      tr: 'Mühendislik öğrencilerine yönelik savunma sanayii profesyonel gelişim programı.',
      en: 'A defense industry professional development program for engineering students.',
    },
    to: '/program/defense-industry-development',
  },
  {
    // Koyu zeminde okunması için markanın BEYAZ varyantı.
    logo: '/prototip-hizlandirma-beyaz.svg',
    accent: '#ffb600',
    title: { tr: 'Prototip Hızlandırma', en: 'Prototype Acceleration' },
    desc: {
      tr: 'Fikirlerin somut prototiplere dönüştürülmesi için hızlandırma desteği.',
      en: 'Acceleration support for turning ideas into working prototypes.',
    },
    to: '/program/prototype-acceleration',
  },
];

// Sabit ana sayfa zemini — kapsul-home.svg illüstrasyonu NEON ÇİZİM olarak.
// Çizgiler başta görünmez; scroll ilerledikçe alt-çizimler stroke-dash ile
// KENDİ DOĞRULTULARINDA çizilir (Zindankale/NeonScene tekniği), en alttaki
// çizgilerden başlayıp yukarı doğru tamamlanır. Footer'a gelindiğinde tamamı
// neon olarak yanmış olur.
// Alt→üst çizim sırası için dikey bant sayısı: illüstrasyonun alt-çizimleri
// (M...M parçaları) başlangıç Y konumlarına göre bantlara ayrılır; en alttaki
// bant önce, en üstteki en son çizilir. Her bant tek <path> olarak birleşir ve
// stroke-dash ile KENDİ ÇİZGİ DOĞRULTUSUNDA çizilir (Zindankale tekniği).
const NEON_BANDS = 40;

// /kapsul-home.svg'yi okur, tek dev path'i alt-çizimlerine ayırıp Y'ye göre
// bantlara dağıtır. bands[0] = EN ALT bant.
const useNeonBands = (url: string, enabled: boolean) => {
  const [data, setData] = useState<{ bands: string[]; viewBox: string }>({
    bands: [],
    viewBox: '0 0 1365 768',
  });

  // 0.95 MB'lık SVG hem indirilip hem DOMParser ile ayrıştırılıyor (ana iş
  // parçacığında). Bu iş SAYFA YÜKLENDİKTEN sonra, tarayıcı boştayken yapılır:
  // dekoratif bir zemin katmanı olduğu için ilk boyamayı geciktirmemeli.
  useEffect(() => {
    if (!enabled) {
      setData({ bands: [], viewBox: '0 0 1365 768' });
      return;
    }
    let alive = true;
    let idle = 0;

    const run = () => {
      if (!alive) return;
      fetch(url)
        .then((r) => r.text())
        .then((text) => {
          const doc = new DOMParser().parseFromString(text, 'image/svg+xml');
          const svg = doc.querySelector('svg');
          const viewBox = svg?.getAttribute('viewBox') || '0 0 1365 768';
          const vbH = Number(viewBox.split(/\s+/)[3]) || 768;

          const subpaths: { d: string; y: number }[] = [];
          svg?.querySelectorAll('path').forEach((el) => {
            const d = el.getAttribute('d') || '';
            const parts = d.match(/[Mm][^Mm]*/g);
            if (parts)
              parts.forEach((sp) => {
                const m = sp.match(/[Mm]\s*([-\d.]+)[ ,]([-\d.]+)/);
                subpaths.push({ d: sp, y: m ? Number(m[2]) : 0 });
              });
          });

          const buckets: { d: string; y: number }[][] = Array.from(
            { length: NEON_BANDS },
            () => [],
          );
          subpaths.forEach((sp) => {
            // Alt-çizimin başlangıç Y'si → bant indeksi (0 = en alt).
            const band = Math.min(
              NEON_BANDS - 1,
              Math.max(
                0,
                NEON_BANDS - 1 - Math.floor((sp.y / vbH) * NEON_BANDS),
              ),
            );
            buckets[band].push(sp);
          });

          // Bant içinde de ALTTAN yukarı sırala → dash çizimi bant içinde bile
          // aynı yönde aksın (parça parça sıçrama yerine akışkan dalga).
          buckets.forEach((b) => b.sort((a, c) => c.y - a.y));

          if (alive)
            setData({
              bands: buckets.map((b) => b.map((s) => s.d).join(' ')),
              viewBox,
            });
        })
        .catch(() => {});
    };

    const ric = (
      window as Window &
        typeof globalThis & {
          requestIdleCallback?: (cb: () => void) => number;
        }
    ).requestIdleCallback;
    const schedule = () => {
      idle = ric ? ric(run) : window.setTimeout(run, 200);
    };
    if (document.readyState === 'complete') schedule();
    else window.addEventListener('load', schedule, { once: true });

    return () => {
      alive = false;
      window.removeEventListener('load', schedule);
      window.clearTimeout(idle);
    };
  }, [url, enabled]);

  return data;
};

// Sayfanın SABİT (viewport'a çakılı) zemin degradesi. Neon kenar çizgisinin
// altındaki dolgu da bunun BİREBİR aynısını kullanır → çizginin altında ton
// farkı/dikiş kalmaz.
const PAGE_BACKDROP =
  'radial-gradient(120% 90% at 50% -10%, #0a1d3a 0%, #051026 44%, #03060f 100%)';

const useCompactHomeMode = () => {
  const [compact, setCompact] = useState(() => {
    if (typeof window === 'undefined') return false;
    const saveData =
      (navigator as Navigator & { connection?: { saveData?: boolean } })
        .connection?.saveData ?? false;
    return (
      window.matchMedia('(prefers-reduced-motion: reduce)').matches || saveData
    );
  });

  useEffect(() => {
    const media = window.matchMedia('(prefers-reduced-motion: reduce)');
    const update = () => {
      const saveData =
        (navigator as Navigator & { connection?: { saveData?: boolean } })
          .connection?.saveData ?? false;
      setCompact(media.matches || saveData);
    };
    update();
    media.addEventListener('change', update);
    return () => media.removeEventListener('change', update);
  }, []);

  return compact;
};

const HomeBackdrop = ({ bare = false }: { bare?: boolean }) => {
  const [p, setP] = useState(0); // sayfa scroll ilerlemesi 0→1
  const [drawNeon, setDrawNeon] = useState(
    () =>
      typeof window !== 'undefined' &&
      window.matchMedia(
        '(min-width: 768px) and (prefers-reduced-motion: no-preference)',
      ).matches,
  );
  const { bands, viewBox } = useNeonBands('/kapsul-home.svg', drawNeon);

  useEffect(() => {
    const media = window.matchMedia(
      '(min-width: 768px) and (prefers-reduced-motion: no-preference)',
    );
    const update = () => setDrawNeon(media.matches);
    update();
    media.addEventListener('change', update);
    return () => media.removeEventListener('change', update);
  }, []);

  useEffect(() => {
    if (!drawNeon) {
      setP(0);
      return;
    }
    let raf = 0;
    let target = 0;
    let current = 0;

    const measure = () => {
      const doc = document.documentElement;
      const vh = window.innerHeight;
      // Frame (pinli) açılış bölümü bitene kadar ilerleme 0 sayılır; çizim
      // ancak arka plan gerçekten görünür olduktan sonra başlar. FOOTER
      // görünür alana girdiği anda 0→1 TAMAMLANMIŞ olur.
      // 3.6vh = sheet'in frame üzerine binmeye BAŞLADIĞI nokta (parkur 460vh,
      // bindirme 100lvh). Ondan önce zemin zaten kare dizisiyle örtülüdür.
      const start = vh * 3.6;
      const footer = document.querySelector('footer');
      const end = footer
        ? footer.getBoundingClientRect().top + window.scrollY - vh
        : doc.scrollHeight - vh;
      const max = Math.max(start + 1, end);
      target = Math.min(
        1,
        Math.max(0, (window.scrollY - start) / (max - start)),
      );
    };

    // Sürekli lerp — hedefe yumuşakça yaklaşır; çizim dalga gibi akar.
    // Hedefe yeterince yaklaşınca DEĞERE SABİTLENİR — asimptotik kalıp
    // çizgilerin son parçasını yarım bırakmasın (tamamlama sorunu).
    const tick = () => {
      current += (target - current) * 0.07;
      if (Math.abs(target - current) > 0.0004) {
        setP(current);
      } else if (current !== target) {
        current = target;
        setP(target);
      }
      raf = requestAnimationFrame(tick);
    };

    measure();
    current = target;
    setP(current);
    raf = requestAnimationFrame(tick);
    window.addEventListener('scroll', measure, { passive: true });
    window.addEventListener('resize', measure);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('scroll', measure);
      window.removeEventListener('resize', measure);
    };
  }, [drawNeon]);

  // Her bandın çizim penceresi: EN ALT ve EN ÜST bantlar önce başlar, çizim
  // iki uçtan ORTAYA doğru tamamlanır. Pencereler bolca üst üste biner →
  // kesintisiz, dalga gibi akışkan bir tamamlanma oluşur.
  // NOT: son pencere TAM p=1'de biter (0.65+0.35) → paydaşlara gelindiğinde
  // hiçbir bant yarım kalmaz (eskiden 0.68+0.34=1.02 → tamamlanamıyordu).
  const bandT = (i: number) => {
    // Kenara uzaklık: 0 = en alt VEYA en üst bant, max = orta bant.
    const order = Math.min(i, NEON_BANDS - 1 - i);
    const maxOrder = Math.floor((NEON_BANDS - 1) / 2);
    const start = (order / Math.max(1, maxOrder)) * 0.65;
    const end = start + 0.35;
    return Math.min(1, Math.max(0, (p - start) / (end - start)));
  };

  return (
    <div
      aria-hidden
      // bare kopya BÖLÜMÜN İÇİNDE kullanılır: fixed kalırsa kırpılan kenarında
      // ince bir dikiş çizgisi oluşuyordu → absolute.
      className={`pointer-events-none z-0 overflow-hidden ${bare ? 'absolute inset-0' : 'fixed inset-0'}`}
      style={{
        background: bare ? undefined : PAGE_BACKDROP,
      }}
    >
      {/* NEON ÇİZİM — Zindankale tekniği: alt-çizimler stroke-dash ile kendi
          doğrultularında çizilir; alt bantlardan başlayıp yukarı tamamlanır. */}
      <svg
        viewBox={viewBox}
        // "slice" geniş ekranda ALT bandı kırpıyordu → sağ alttaki çizgiler
        // hiç görünmüyordu. "none" ile illüstrasyon ekrana TAM oturur,
        // hiçbir bölge kesilmez (0.14 opaklıkta hafif esneme fark edilmez).
        preserveAspectRatio="none"
        className="absolute inset-0 h-full w-full"
        fill="none"
        style={{ opacity: 0.14 }}
      >
        <g>
          {drawNeon &&
            bands.map((d, i) => {
              const t = bandT(i);
              if (!d || t <= 0) return null;
              return (
                <path
                  key={i}
                  d={d}
                  pathLength={1}
                  stroke="#5f9bff"
                  strokeWidth={1.1}
                  strokeLinecap="round"
                  strokeDasharray="1"
                  strokeDashoffset={1 - t}
                />
              );
            })}
        </g>
      </svg>
      {/* Yumuşak azure parıltı */}
      <span
        className="absolute left-1/2 top-1/2 h-[54vmax] w-[54vmax] -translate-x-1/2 -translate-y-1/2 rounded-full"
        style={{
          background:
            'radial-gradient(circle, rgba(61,132,255,0.1), transparent 66%)',
        }}
      />
      {/* Üst/alt vinyet — navbar ve içerik okunurluğu */}
      <div
        className="absolute inset-x-0 top-0 h-44"
        style={{
          background:
            'linear-gradient(to bottom, rgba(3,6,15,0.75), transparent)',
        }}
      />
      <div
        className="absolute inset-x-0 bottom-0 h-44"
        style={{
          background: 'linear-gradient(to top, rgba(3,6,15,0.75), transparent)',
        }}
      />
    </div>
  );
};

const MobileHomeHero = () => {
  const { t } = useTranslation();

  useEffect(() => {
    window.dispatchEvent(new Event('ktp-home-frame-ready'));
  }, []);

  return (
    <section className="relative min-h-[100svh] overflow-hidden bg-black pt-20">
      <img
        src={`${process.env.PUBLIC_URL}/frames/home/frame_001.webp`}
        alt=""
        aria-hidden
        width={1920}
        height={1080}
        decoding="async"
        fetchPriority="high"
        className="absolute inset-0 h-full w-full object-cover opacity-70"
      />
      <span
        aria-hidden
        className="absolute inset-0 bg-gradient-to-b from-black/55 via-black/20 to-[#03060f]"
      />
      <div className="relative z-10 flex min-h-[calc(100svh-5rem)] flex-col items-center justify-center px-5 text-center">
        <h1
          className="max-w-4xl font-bricolage text-3xl font-semibold leading-[1.08] tracking-tight text-white sm:text-5xl"
          style={{
            textShadow:
              '0 2px 18px rgba(0,0,0,0.9), 0 1px 3px rgba(0,0,0,0.95)',
          }}
        >
          {t('home.frameCaption')}
        </h1>
        <p
          className="mt-5 max-w-2xl font-bricolage text-base font-medium leading-relaxed text-white/82 sm:text-xl"
          style={{
            textShadow:
              '0 2px 16px rgba(0,0,0,0.9), 0 1px 3px rgba(0,0,0,0.95)',
          }}
        >
          {t('home.frameParagraph')}
        </p>
      </div>
    </section>
  );
};

// Sheet'in üst kenarındaki NEON çizgi — AÇILIŞ EKRANINDAKİ yapı: yatay neon
// çizgi ortada logo kapsülüne (yuvarlatılmış altıgen) açılır. Burada o yapının
// ALT konturu çizilir; kapsülün dibi geçiş kenarına oturur ve sheet yukarı
// çıktıkça kontur kademeli düzleşip kenarda DÜZ bir neon çizgiye dönüşür.
const CAPSULE_VB_H = 220; // bant yüksekliği (px)
const CAPSULE_ARM_Y = 20; // kol çizgisinin y'si (k=1)

// Kapsül alt konturu — GERÇEK PİKSEL koordinatlarında, TEĞET-SÜREKLİ
// köşelerle üretilir: her köşe, iki doğrunun kesişim VERTEKSİ kontrol noktası
// olan quadratic eğridir → doğru-eğri geçişlerinde kırılma OLMAZ. k ölçeği
// uniform (x ve y birlikte) uygulandığından kapanırken de açı/teğet korunur.
//
// Taban geometri (k=1, merkeze göre): kol y=20, dip y=200, diyagonal eğim
// 110/100; verteksler T(-302,20) ve B(-138,200), köşe yarıçapı 45.
// Açıklık ölçeği — kapsül çentiğinin genel boyutu (küçültmek için düşür).
const CAPSULE_SCALE = 0.6;
// Orta (taban) düzlüğünün ek yarı genişliği — ortayı genişletir; derinlik,
// eğim ve köşeler değişmez.
const CAPSULE_WIDEN = 150;

// f: ekran genişliğine göre uniform küçültme çarpanı (dar ekranda kapsül
// tamamen görünür kalsın diye; f=1 masaüstü referansı).
const capsulePts = (f: number) => {
  const s = CAPSULE_SCALE * f;
  const u = { x: 100 / 148.66, y: 110 / 148.66 }; // diyagonal birim vektörü
  const r = 45 * s;
  const T = { x: -302 * s - CAPSULE_WIDEN * f, y: CAPSULE_ARM_Y };
  const B = { x: -138 * s - CAPSULE_WIDEN * f, y: CAPSULE_ARM_Y + 180 * s };
  return {
    S1: { x: T.x - r, y: T.y }, // kol → üst köşe girişi
    T, // üst köşe verteksi (Q kontrolü)
    E1: { x: T.x + r * u.x, y: T.y + r * u.y }, // üst köşe çıkışı
    S2: { x: B.x - r * u.x, y: B.y - r * u.y }, // alt köşe girişi
    B, // alt köşe verteksi (Q kontrolü)
    E2: { x: B.x + r, y: B.y }, // alt köşe çıkışı → taban
  };
};

const CAPSULE_EDGE_Y = 210; // kol çizgisi = geçiş kenarı (bant altına sabit)

// Kapsülün ekran genişliğine göre uniform ölçeği. Neon çizgi kalınlıkları da
// bununla ölçeklenir; aksi halde dar ekranda küçülen şeklin üzerinde sabit
// kalınlıktaki hale şekli yutup masaüstünden bambaşka görünüyordu.
const capsuleScale = (w: number) => Math.min(1, Math.max(0.46, w / 1400));

const capsulePath = (k: number, w: number) => {
  const cx = w / 2;
  // Dar ekranda kapsül ekrandan taşıp merkezsiz görünüyordu: şekli genişlikle
  // birlikte uniform küçült (masaüstünde f=1 → görünüm birebir aynı).
  const f = capsuleScale(w);
  const P = capsulePts(f);
  const ax = P.E2.x; // tepe düzlüğü ucu çapa: x'i k'den bağımsız
  // ÖNCEKİ yön: kapsül AŞAĞI açılır — dip geçiş kenarına sabit, kollar
  // yukarıda durur; k→0'da kollar kenara inerek çizgi düzleşir.
  // Ölçek uniform → açı/teğet korunur.
  const Y = (y0: number) => CAPSULE_EDGE_Y - (P.B.y - y0) * k;
  const armY = Y(CAPSULE_ARM_Y);
  const L = (p: { x: number; y: number }) =>
    `${cx + ax + (p.x - ax) * k} ${Y(p.y)}`;
  const R = (p: { x: number; y: number }) =>
    `${cx - ax - (p.x - ax) * k} ${Y(p.y)}`;
  const { S1, T, E1, S2, B, E2 } = P;
  return [
    `M0 ${armY} L${L(S1)}`,
    `Q${L(T)} ${L(E1)}`,
    `L${L(S2)}`,
    `Q${L(B)} ${L(E2)}`,
    `L${R(E2)}`,
    `Q${R(B)} ${R(S2)}`,
    `L${R(E1)}`,
    `Q${R(T)} ${R(S1)}`,
    `L${w} ${armY}`,
  ].join(' ');
};

const SheetNeonEdge = () => {
  const ref = useRef<HTMLDivElement>(null);
  const [k, setK] = useState(1); // 1 = tam şekil, 0 = düz çizgi
  const [w, setW] = useState(0); // gerçek piksel genişliği (esnetme yok)
  useEffect(() => {
    let raf = 0;
    const measure = () => {
      const el = ref.current;
      if (!el) return;
      setW(el.clientWidth);
      const top = el.getBoundingClientRect().top;
      const vh = window.innerHeight;
      // Kenar ekranın altındayken tam şekilli; yukarı çıktıkça düzleşir.
      setK(Math.min(1, Math.max(0, top / (vh * 0.92))));
    };
    const onScroll = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(measure);
    };
    measure();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
    };
  }, []);

  const d = w > 0 ? capsulePath(k, w) : '';
  // Hale/çekirdek kalınlıkları da kapsülle aynı oranda küçülür.
  const s = capsuleScale(w || 1400);
  return (
    <div
      ref={ref}
      aria-hidden
      // bottom-full (transform DEĞİL): transform, içindeki fixed katmanı
      // kendine bağlayıp viewport zeminini kaydırırdı.
      className="pointer-events-none absolute inset-x-0 bottom-full z-30 overflow-visible"
      style={{ height: CAPSULE_VB_H }}
    >
      {d && (
        <svg
          viewBox={`0 0 ${w} ${CAPSULE_VB_H}`}
          className="absolute inset-0 h-full w-full overflow-visible"
          fill="none"
        >
          {/* Çizginin ALTI DOLU — üstteki kare dizisinin zemini olan SAF
              SİYAH. Sheet de üstten siyah başlayıp degradeye erediği için
              (SHEET_FADE) hiçbir yönde ton farkı kalmaz. */}
          <path d={`${d} V${CAPSULE_VB_H} H0 Z`} fill="#000" />
          {/* Beyaz çekirdek + açık mavi-beyaz iç parıltı + mavi dış hale */}
          <g>
            {/* Dış hale — mavi */}
            <path
              d={d}
              stroke="#4f7dff"
              strokeWidth={14 * s}
              style={{ filter: `blur(${14 * s}px)`, opacity: 0.6 }}
            />
            {/* İç parıltı — mavi-beyaz */}
            <path
              d={d}
              stroke="#b9d0ff"
              strokeWidth={5 * s}
              style={{ filter: `blur(${4 * s}px)`, opacity: 0.85 }}
            />
            {/* Çekirdek — BEYAZ neon */}
            <path
              d={d}
              stroke="#ffffff"
              strokeWidth={Math.max(1.4, 2 * s)}
              strokeLinecap="round"
              style={{ filter: `drop-shadow(0 0 ${6 * s}px #6b9aff)` }}
            />
          </g>
        </svg>
      )}
    </div>
  );
};

const clamp01 = (v: number) => Math.min(1, Math.max(0, v));
const easeOut3 = (x: number) => 1 - (1 - x) ** 3;

// PİNLİ BAŞLIK SAHNESİ (230vh parkur): bölüme gelindiğinde başlık ekran
// ortasında BÜYÜK belirir; scroll ile küçülüp normal konumuna yerleşir ve
// kartlar 3D perspektif dönüşüyle (rotateX + yükselme) sırayla yerini alır.
// Destek / Gelişim / Etkinlikler / Basın bölümlerinin ortak sahnesi.
const StageSection = ({
  title,
  gridClass,
  items,
  footer,
  cover = false,
  flat = false,
  mobileCarousel = false,
}: {
  title: string;
  /** Kart ızgarasının genişlik/kolon sınıfları */
  gridClass: string;
  items: React.ReactNode[];
  /** Kartlardan sonra beliren alt öğe (ör. "Tümünü gör") */
  footer?: React.ReactNode;
  /** Sheet'in İLK yüzü: frame'in üzerine binerken arkasını örten OPAK zemin */
  cover?: boolean;
  /** 3D dönüş YERİNE düz yükselme — kendi transform/WebGL'i olan kartlar
      (ContentCard/WaterImage) 3D sarmalayıcıda bozulduğu için */
  flat?: boolean;
  /** Mobilde ızgara yerine yatay kaydırmalı (snap) karusel — geniş görselli
      kartlar (etkinlik/basın) dar ekranda alt alta upuzun yığılmasın diye */
  mobileCarousel?: boolean;
}) => {
  const ref = useRef<HTMLElement>(null);
  const stickyRef = useRef<HTMLDivElement>(null);
  const wrapRef = useRef<HTMLDivElement>(null);
  const titleRef = useRef<HTMLHeadingElement>(null);
  const [p, setP] = useState(0);
  // Başlığın yuvası ile ekran merkezi arasındaki dikey fark (px) — başlık
  // TEK öğedir; büyük halde ekran ortasında başlar, kendi yuvasına iner.
  const [dy, setDy] = useState(0);

  useEffect(() => {
    let raf = 0;
    const onScroll = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        const el = ref.current;
        if (!el) return;
        const r = el.getBoundingClientRect();
        const denom = Math.max(1, r.height - window.innerHeight);
        setP(clamp01(-r.top / denom));
        // Yuva konumunu STICKY konteynere göre TRANSFORMSUZ ölç: iki rect
        // birlikte kaydığından değer scroll'dan bağımsız SABİTTİR → yazı
        // JS ile ekrana sabitlenmez, titreme oluşmaz. Sticky pinliyken
        // konteyner merkezi zaten ekran merkezidir.
        const wrap = wrapRef.current;
        const h2 = titleRef.current;
        const sticky = stickyRef.current;
        if (wrap && h2 && sticky) {
          const slotCenter =
            wrap.getBoundingClientRect().top -
            sticky.getBoundingClientRect().top +
            h2.offsetTop +
            h2.offsetHeight / 2;
          const next = slotCenter - sticky.clientHeight / 2;
          setDy((prev) => (Math.abs(prev - next) > 0.5 ? next : prev));
        }
      });
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
    };
  }, []);

  // Faz 1 — başlık TEK öğe: ekran ortasında BÜYÜK başlar, scroll ile kendi
  // yuvasına inip normal boyutuna döner (kopya yok → üst üste binme imkânsız).
  const settle = easeOut3(clamp01(p / 0.42));
  const late = clamp01((p - 0.38) / 0.1); // ayraç çizgisi otururken belirir
  // Taban punto lg+'ta büyüdüğü için (text-5xl) ölçek tepesi düşürüldü:
  // animasyonun en büyük anı eskisiyle aynı genişlikte kalsın.
  const BIG = 1.65; // başlığın başlangıç ölçeği
  // Faz 2 — kartlar: 0.48'den sonra sırayla 3D dönüşle gelir
  const cardT = (i: number) => easeOut3(clamp01((p - 0.48 - i * 0.1) / 0.24));
  // Alt öğe (footer) — son karttan sonra belirir
  const footerT = easeOut3(clamp01((p - 0.48 - items.length * 0.1) / 0.2));

  return (
    <>
      {/* ── MASAÜSTÜ (lg+): pinli sahne — başlık ortada büyük, kartlar 3D.
          Mobilde KULLANILMAZ: sticky+overflow-hidden, alt alta dizilen
          kartları kırptığı için içerik görünmüyordu. ── */}
      <section ref={ref} className="relative hidden lg:block lg:h-[230vh]">
        <div
          ref={stickyRef}
          className="sticky top-0 flex h-[100lvh] flex-col items-center justify-center overflow-hidden px-4 sm:px-6"
        >
          {/* Örtücü zemin — frame ARKADAYKEN opak kalır. Bindirme 100vh, pin
              parkuru 130vh → frame ancak p≈0.77'de tamamen geçilir; örtü
              ONDAN SONRA sönerek arka plandaki neon illüstrasyonu açar
              (erken sönerse frame ile içerik üst üste biner = çakışma). */}
          {cover && (
            <div
              aria-hidden
              className="pointer-events-none absolute inset-0"
              style={{
                background: 'linear-gradient(180deg, #000000 0%, #03060f 38%)',
                opacity: 1 - clamp01((p - 0.8) / 0.15),
              }}
            />
          )}
          {/* Başlık bloğu — h2 transform ile ortadan yuvasına iner.
              NOT: relative şart — h2.offsetTop bu sarmalayıcıya göre ölçülür */}
          <div ref={wrapRef} className="relative text-center">
            <h2
              ref={titleRef}
              className="font-bricolage text-3xl font-bold leading-[1.12] tracking-tight text-white sm:text-4xl lg:text-5xl"
              style={{
                transform: `translateY(${-(1 - settle) * dy}px) scale(${1 + (BIG - 1) * (1 - settle)})`,
                transformOrigin: 'center',
                textShadow: `0 0 ${40 * (1 - settle)}px ${ACCENT}44`,
              }}
            >
              {title}
            </h2>
            <div
              className="mx-auto mt-7 h-px w-40"
              style={{
                background: `linear-gradient(to right, transparent, ${ACCENT}66, transparent)`,
                opacity: late,
              }}
            />
          </div>

          {/* Kartlar — 3D perspektif dönüşüyle (flat: düz yükselme) yerleşir */}
          <div
            className={`mx-auto mt-12 grid w-full gap-6 ${gridClass}`}
            style={flat ? undefined : { perspective: '1200px' }}
          >
            {items.map((item, i) => {
              const ci = cardT(i);
              return (
                <div
                  key={i}
                  style={
                    flat
                      ? {
                          opacity: ci,
                          transform:
                            ci >= 1
                              ? undefined
                              : `translateY(${(1 - ci) * 60}px)`,
                          pointerEvents: ci > 0.6 ? 'auto' : 'none',
                        }
                      : {
                          opacity: ci,
                          transform: `translateY(${(1 - ci) * 90}px) rotateX(${(1 - ci) * 42}deg) scale(${0.86 + 0.14 * ci})`,
                          transformStyle: 'preserve-3d',
                          pointerEvents: ci > 0.6 ? 'auto' : 'none',
                        }
                  }
                >
                  {item}
                </div>
              );
            })}
          </div>

          {/* Alt öğe (ör. Tümünü gör) — kartlardan sonra belirir */}
          {footer && (
            <div
              style={{
                opacity: footerT,
                pointerEvents: footerT > 0.6 ? 'auto' : 'none',
              }}
            >
              {footer}
            </div>
          )}
        </div>
      </section>

      {/* ── MOBİL/TABLET (lg altı): normal akış — pin yok, kırpılma yok.
          Başlık masaüstündeki gibi scroll'a bağlı "büyükten yuvaya oturma"
          hissiyle gelir (MobileStageTitle); kartlar Reveal ile yükselir,
          mobileCarousel açıkken yatay snap karuselde kayar ── */}
      <section className="relative px-4 py-16 sm:px-6 lg:hidden">
        <MobileStageTitle title={title} />
        {mobileCarousel ? (
          <div className="-mx-4 mt-8 sm:-mx-6">
            <div className="flex snap-x snap-mandatory gap-4 overflow-x-auto px-6 pb-3 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
              {items.map((item, i) => (
                <div
                  key={i}
                  className="w-[78%] flex-none snap-center sm:w-[46%]"
                >
                  <Reveal delay={i * 90} from="rise">
                    {item}
                  </Reveal>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div
            className={`mx-auto mt-10 grid w-full gap-4 sm:gap-6 ${gridClass}`}
          >
            {items.map((item, i) => (
              <Reveal key={i} delay={i * 70} from="rise">
                {item}
              </Reveal>
            ))}
          </div>
        )}
        {footer && <Reveal delay={items.length * 70}>{footer}</Reveal>}
      </section>
    </>
  );
};

const MediaPlaceholderCard = () => (
  <div className="relative aspect-[3/4] overflow-hidden rounded-3xl bg-slate-900 shadow-lg ring-1 ring-white/10">
    <div className="absolute inset-0 animate-pulse bg-white/[0.045]" />
    <span
      aria-hidden
      className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent"
    />
    <div className="absolute inset-x-0 bottom-0 space-y-3 p-5 sm:p-6">
      <div className="h-3 w-20 animate-pulse rounded bg-white/15" />
      <div className="h-5 w-4/5 animate-pulse rounded bg-white/18" />
      <div className="h-3 w-full animate-pulse rounded bg-white/12" />
      <div className="h-3 w-2/3 animate-pulse rounded bg-white/12" />
    </div>
  </div>
);

// Mobil bölüm başlığı — masaüstündeki pinli sahnenin "büyük gelip yuvasına
// oturma" duygusunun hafif sürümü: başlık görünüme girerken scroll'a bağlı
// olarak 1.45×'ten normale iner, glow'u sönerek yerleşir.
const MobileStageTitle = ({ title }: { title: string }) => {
  const ref = useRef<HTMLDivElement>(null);
  const [t, setT] = useState(0);
  useEffect(() => {
    let raf = 0;
    const onScroll = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        const el = ref.current;
        if (!el) return;
        const r = el.getBoundingClientRect();
        const vh = window.innerHeight;
        // Alttan girerken 0 → ekranın üst %35'ine yaklaşınca 1.
        setT(easeOut3(clamp01((vh - r.top) / (vh * 0.6))));
      });
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
    };
  }, []);
  return (
    <div ref={ref} className="text-center">
      <h2
        className="font-bricolage text-2xl font-bold leading-[1.12] tracking-tight text-white sm:text-3xl"
        style={{
          opacity: 0.2 + t * 0.8,
          transform: `scale(${1.45 - 0.45 * t}) translateY(${(1 - t) * 18}px)`,
          transformOrigin: 'center',
          textShadow: `0 0 ${28 * (1 - t)}px ${ACCENT}55`,
        }}
      >
        {title}
      </h2>
      <div
        className="mx-auto mt-5 h-px w-32"
        style={{
          background: `linear-gradient(to right, transparent, ${ACCENT}66, transparent)`,
          opacity: t,
        }}
      />
    </div>
  );
};

// Paydaşlar pinli sahnesi — 220vh parkur. Kendi scroll ölçümüyle:
// parkurun %42–%56 aralığında paydaşlar TAMAMEN söner ve gizlenir; footer
// panelleri (%60'ta başlar) boş sahnenin üzerinde kapanır.
const StakeholdersStage = () => {
  const { t } = useTranslation();
  const ref = useRef<HTMLElement>(null);
  const [raw, setRaw] = useState(0);
  const [fade, setFade] = useState(0);
  // Mobilde başlık büyüyüp oturma animasyonu yok: başlık doğrudan üst
  // yuvasında küçük durur, sahne hemen açılır (dar ekranda dev başlık
  // parkurun ilk yarısını kaplıyordu).
  const [mobile, setMobile] = useState(
    () =>
      typeof window !== 'undefined' &&
      !window.matchMedia('(min-width: 768px)').matches,
  );

  useEffect(() => {
    let raf = 0;
    const desktopMedia = window.matchMedia('(min-width: 1024px)');
    const mobileMedia = window.matchMedia('(max-width: 767px)');
    const updateMobile = () => setMobile(mobileMedia.matches);
    updateMobile();
    mobileMedia.addEventListener('change', updateMobile);
    const onScroll = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        const el = ref.current;
        if (!el) return;
        const r = el.getBoundingClientRect();
        const vh = window.innerHeight;
        const denom = Math.max(1, r.height - vh);
        const rw = Math.min(1, Math.max(0, -r.top / denom));
        setRaw((prev) => (Math.abs(prev - rw) > 0.003 ? rw : prev));
        // Sönme yalnız lg+: masaüstünde footer panelleri paydaşların üzerine
        // kapanır. Mobilde footer normal akışta → sönme boş scroll bırakıyordu.
        const nextFade = desktopMedia.matches
          ? Math.min(1, Math.max(0, (rw - 0.42) / 0.14))
          : 0;
        setFade((prev) =>
          Math.abs(prev - nextFade) > 0.003 ? nextFade : prev,
        );
      });
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    return () => {
      cancelAnimationFrame(raf);
      mobileMedia.removeEventListener('change', updateMobile);
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
    };
  }, []);

  // ANA SAYFA akışı: başlık ekran ORTASINDA büyük belirir, üstteki yuvasına
  // inip küçülür; alt başlık + ayraç belirir, ardından paydaş sahnesi açılır.
  const settle = mobile ? 1 : easeOut3(clamp01(raw / 0.16));
  const late = mobile ? 1 : clamp01((raw - 0.13) / 0.06);
  const sceneIn = mobile ? 1 : easeOut3(clamp01((raw - 0.16) / 0.1));

  return (
    <section ref={ref} className="relative h-[170vh] lg:h-[260vh]">
      <div
        className="sticky top-0 h-[100lvh] overflow-hidden"
        style={{
          opacity: 1 - fade,
          visibility: fade >= 1 ? 'hidden' : 'visible',
        }}
      >
        {/* Paydaş sahnesi — başlık oturduktan sonra belirir */}
        <div className="absolute inset-0" style={{ opacity: sceneIn }}>
          <Stakeholders />
        </div>

        {/* Başlık — diğer bölümlerdeki akış: büyükten üst yuvaya oturur */}
        <div className="pointer-events-none absolute inset-x-0 top-[15vh] z-10 px-4 text-center">
          <h2
            className="font-bricolage text-3xl font-bold leading-[1.12] tracking-tight text-white sm:text-4xl lg:text-5xl"
            style={{
              transform: `translateY(${(1 - settle) * 36}vh) scale(${1 + 0.65 * (1 - settle)})`,
              transformOrigin: 'center',
              textShadow: `0 0 ${40 * (1 - settle)}px ${ACCENT}44`,
            }}
          >
            {t('home.stakeholders')}
          </h2>
          <div
            className="mx-auto mt-5 h-px w-40"
            style={{
              background: `linear-gradient(to right, transparent, ${ACCENT}66, transparent)`,
              opacity: late,
            }}
          />
        </div>
      </div>
    </section>
  );
};

// "Tümünü gör" hap butonu — footer CTA'sıyla aynı dil, azure.
const SeeAll = ({ to, label }: { to: string; label: string }) => (
  <div className="mt-10 flex justify-center">
    <Link
      to={to}
      className="group inline-flex items-center gap-2 rounded-full px-7 py-3 text-sm font-semibold text-white transition-all hover:-translate-y-0.5"
      style={{ background: ACCENT, boxShadow: `0 18px 50px -22px ${ACCENT}` }}
    >
      {label}
      <ArrowRightOutlined className="text-xs transition-transform group-hover:translate-x-0.5" />
    </Link>
  </div>
);

// ── KAPSÜL DESTEK PROGRAMLARI — sticky-split anlatım: solda numaralı adım
// metinleri (çapraz geçiş), sağda yapışkan medya paneli. Panel, GİDİLEN
// program sayfasının arka plan diliyle çizilir (aynı radial zemin + accent
// ızgara + nefes alan parıltılar) → karttan sayfaya kesintisiz geçiş hissi.
const SUPPORT_STEP_SCENES = [
  {
    panelBg:
      'radial-gradient(120% 90% at 50% -10%, #2a1204 0%, #150c08 44%, #070608 100%)',
    grid: 'rgba(254,140,60,0.06)',
  },
  {
    panelBg:
      'radial-gradient(120% 90% at 50% -10%, #0b2d1f 0%, #081711 44%, #050a08 100%)',
    grid: 'rgba(110,230,175,0.06)',
  },
  {
    panelBg:
      'radial-gradient(110% 95% at 78% -10%, #27130a 0%, #150c09 45%, #070607 100%)',
    grid: 'rgba(255,150,70,0.06)',
  },
];

// Destek kartı silüeti — verilen PNG: köşeleri yuvarlatılmış kart; SOL kenarında
// S-kavisli içe ÇENTİK. Çentiğin dikey konumu adım değiştikçe kayar (üst →
// orta → alt) ve clip-path geçişiyle akışkan biçimde yer değiştirir.
const CARD_NOTCH_D = 26; // çentik derinliği
const CARD_R = 34; // köşe yarıçapı
// Adım sırasına göre çentik merkezinin dikey oranı: üst, orta, alt döngüsü.
const NOTCH_SPOTS = [0.28, 0.5, 0.72];
const supportCardPath = (W: number, H: number, spot: number) => {
  const R = Math.min(CARD_R, W / 6, H / 8);
  const D = CARD_NOTCH_D;
  const half = Math.min(H * 0.19, 150); // çentik yarı yüksekliği
  const T = 46; // S-geçiş uzunluğu
  const cy = H * spot;
  const nT = Math.max(R + 8, cy - half);
  const nB = Math.min(H - R - 8, cy + half);
  return [
    `M ${R} 0`,
    `L ${W - R} 0`,
    `Q ${W} 0 ${W} ${R}`,
    `L ${W} ${H - R}`,
    `Q ${W} ${H} ${W - R} ${H}`,
    `L ${R} ${H}`,
    `Q 0 ${H} 0 ${H - R}`,
    `L 0 ${nB}`,
    `C 0 ${nB - T * 0.6} ${D} ${nB - T * 0.4} ${D} ${nB - T}`,
    `L ${D} ${nT + T}`,
    `C ${D} ${nT + T * 0.4} 0 ${nT + T * 0.6} 0 ${nT}`,
    `L 0 ${R}`,
    `Q 0 0 ${R} 0`,
    'Z',
  ].join(' ');
};

// Aynı çentiğin YATAY kenar (üst/alt) varyantı — dikey dizilen kartlar için.
// Ölçüler kartın GENİŞLİĞİNDEN türetilir; S-geçişi çentik açıklığına göre
// kısaltılır (dar kartta geçiş eğrileri birbirine binip şekli bozuyordu).
const cardNotchPathV = (
  W: number,
  H: number,
  spot: number,
  side: 'top' | 'bottom',
) => {
  const R = Math.min(CARD_R, W / 8, H / 6);
  const D = Math.min(26, Math.max(16, H * 0.11)); // yatay kenar çentiği
  const half = Math.min(W * 0.22, 150);
  const cx = W * spot;
  const nL = Math.max(R + 8, cx - half);
  const nR = Math.min(W - R - 8, cx + half);
  const T = Math.min(46, (nR - nL) / 2.4);
  const y = (v: number) => (side === 'top' ? v : H - v);
  return [
    `M ${R} ${y(0)}`,
    `L ${nL} ${y(0)}`,
    `C ${nL + T * 0.6} ${y(0)} ${nL + T * 0.4} ${y(D)} ${nL + T} ${y(D)}`,
    `L ${nR - T} ${y(D)}`,
    `C ${nR - T * 0.4} ${y(D)} ${nR - T * 0.6} ${y(0)} ${nR} ${y(0)}`,
    `L ${W - R} ${y(0)}`,
    `Q ${W} ${y(0)} ${W} ${y(R)}`,
    `L ${W} ${y(H - R)}`,
    `Q ${W} ${y(H)} ${W - R} ${y(H)}`,
    `L ${R} ${y(H)}`,
    `Q 0 ${y(H)} 0 ${y(H - R)}`,
    `L 0 ${y(R)}`,
    `Q 0 ${y(0)} ${R} ${y(0)}`,
    'Z',
  ].join(' ');
};

const SUPPORT_STEPS = SUPPORT_BOX_CARDS.map((c, i) => ({
  ...c,
  n: `0${i + 1}`,
  ...SUPPORT_STEP_SCENES[i],
}));

// Kaydırdıkça "yazılıyormuş" gibi beliren metin — ScrollFrames'teki (ana
// sayfa frame'leri) efektin aynısı: yazma kafasının önündeki LEAD harf hayalet
// gibi azalan opaklıkla gelir. Farkı: henüz yazılmamış kısım tamamen gizli
// değil, silik gri durur (blok yüksekliği sabit kalsın ve metin okunabilsin).
const TYPE_LEAD = 4;
const TypedText = ({ text, t }: { text: string; t: number }) => {
  const head = Math.floor(text.length * clamp01(t));
  return (
    <>
      {Array.from(text).map((ch, i) => {
        const d = i - head; // 0 = kafa harfi, <0 yazıldı, >0 ileride
        const op =
          d <= 0
            ? 1
            : d > TYPE_LEAD
              ? 0.18
              : Math.max(0.18, 1 - d / (TYPE_LEAD + 1)) ** 1.3;
        return (
          <span key={i} style={{ opacity: op }}>
            {ch}
          </span>
        );
      })}
    </>
  );
};

// Mobil destek sahnesi — masaüstündeki kurgunun DİKEY hâli ve AYNI animasyon:
// pinli parkur; ÜSTTE adım metni (daktilo efektiyle yazılır, adım değişince
// çapraz geçer), ALTTA tek bir panel EKRANDA KALIR — çentiği ÜST kenarda
// yatayda kayar, panel içeriği (zemin/ızgara/parıltı/logo) çapraz geçer.
const SupportMobileScene = ({
  title,
  lang,
}: {
  title: string;
  lang: 'tr' | 'en';
}) => {
  const trackRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const [p, setP] = useState(0);
  const [box, setBox] = useState({ w: 0, h: 0 });

  useEffect(() => {
    let raf = 0;
    const onScroll = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        const el = trackRef.current;
        if (!el) return;
        const r = el.getBoundingClientRect();
        const dist = r.height - window.innerHeight;
        setP(dist > 0 ? clamp01(-r.top / dist) : 0);
      });
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
    };
  }, []);

  useEffect(() => {
    const el = panelRef.current;
    if (!el) return;
    const ro = new ResizeObserver((entries) => {
      const r = entries[0]?.contentRect;
      if (!r) return;
      setBox((prev) =>
        Math.abs(prev.w - r.width) > 1 || Math.abs(prev.h - r.height) > 1
          ? { w: r.width, h: r.height }
          : prev,
      );
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const N = SUPPORT_STEPS.length;
  // İlk %18: başlık belirir, panel yerine oturur. Sonrası adım akışı.
  const intro = easeOut3(clamp01(p / 0.12));
  const q = clamp01((p - 0.18) / 0.82);
  const step = q * N;
  const idx = Math.min(N - 1, Math.floor(step));
  const local = clamp01(step - idx);
  const write = clamp01(local / 0.28);
  // Çentik yatayda sürekli kayar (masaüstündeki adım adım kaymanın dengi).
  const spot = 0.26 + q * q * (3 - 2 * q) * 0.48;
  const active = SUPPORT_STEPS[idx];
  const path = box.w > 0 ? cardNotchPathV(box.w, box.h, spot, 'top') : '';

  return (
    <div
      ref={trackRef}
      className="relative overflow-x-clip lg:hidden"
      style={{ height: `${N * 110 + 60}vh` }}
    >
      <div className="sticky top-0 flex h-[100lvh] flex-col justify-center overflow-hidden px-5">
        <div className="mx-auto w-full max-w-md" style={{ opacity: intro }}>
          <h2 className="text-center font-bricolage text-2xl font-bold leading-tight tracking-tight text-white">
            {title}
          </h2>

          {/* Adım metni — daktilo efektiyle yazılır */}
          <div className="mt-7 min-h-[8.5rem]">
            <div
              className="text-[10px] font-semibold uppercase tracking-[0.26em]"
              style={{ color: active.accent }}
            >
              {active.n} / 0{N}
            </div>
            {/* Tüm adımların metni aynı ızgara hücresinde üst üste durur, yalnızca
                aktif olan görünür → blok yüksekliği EN UZUN adıma sabitlenir.
                Aksi hâlde adım değişince başlık 1→2 satıra geçip altındaki
                metni ve paneli kaydırıyordu (kaydırma sırasında CLS). */}
            <h3 className="mt-2 grid font-bricolage text-xl font-bold leading-tight text-white">
              {SUPPORT_STEPS.map((s, i) => (
                <span
                  key={i}
                  className={`col-start-1 row-start-1 ${i === idx ? '' : 'invisible'}`}
                >
                  {i === idx ? (
                    <TypedText
                      text={pick(s.title, lang)}
                      t={(local - 0.04) / 0.32}
                    />
                  ) : (
                    pick(s.title, lang)
                  )}
                </span>
              ))}
            </h3>
            <p className="mt-2.5 grid text-sm leading-relaxed text-slate-300/80">
              {SUPPORT_STEPS.map((s, i) => (
                <span
                  key={i}
                  className={`col-start-1 row-start-1 ${i === idx ? '' : 'invisible'}`}
                >
                  {i === idx ? (
                    <TypedText
                      text={pick(s.desc, lang)}
                      t={(local - 0.22) / 0.45}
                    />
                  ) : (
                    pick(s.desc, lang)
                  )}
                </span>
              ))}
            </p>
          </div>

          {/* Panel — sahnede kalır; çentik üstte kayar, içerik çapraz geçer */}
          <Link
            to={active.to}
            aria-label={pick(active.title, lang)}
            className="group mt-5 block"
          >
            <div
              ref={panelRef}
              className="relative aspect-[4/3] w-full overflow-hidden rounded-[26px]"
              style={{
                clipPath: path ? `path('${path}')` : undefined,
                opacity: clamp01(write * 3 + 0.2) * intro,
              }}
            >
              {SUPPORT_STEPS.map((st, i) => (
                <div
                  key={st.to}
                  className="absolute inset-0"
                  style={{
                    background: st.panelBg,
                    opacity: i === idx ? 1 : 0,
                    transition: 'opacity .6s ease',
                  }}
                >
                  <div
                    aria-hidden
                    className="absolute inset-0"
                    style={{
                      backgroundImage: `linear-gradient(${st.grid} 1px, transparent 1px), linear-gradient(90deg, ${st.grid} 1px, transparent 1px)`,
                      backgroundSize: '44px 44px',
                      maskImage:
                        'radial-gradient(90% 80% at 50% 30%, #000 25%, transparent 100%)',
                      WebkitMaskImage:
                        'radial-gradient(90% 80% at 50% 30%, #000 25%, transparent 100%)',
                    }}
                  />
                  <div
                    aria-hidden
                    className="ss-breathe absolute left-1/2 top-[8%] h-[55%] w-[85%] -translate-x-1/2 rounded-full"
                    style={{
                      background: `radial-gradient(closest-side, ${st.accent}26, transparent)`,
                      filter: 'blur(26px)',
                    }}
                  />
                  <div className="absolute inset-0 flex items-center justify-center">
                    <img
                      src={st.logo}
                      alt=""
                      className="h-16 w-auto max-w-[62%] object-contain"
                      style={{ filter: `drop-shadow(0 0 24px ${st.accent}88)` }}
                    />
                  </div>
                  <div
                    className="absolute bottom-5 left-5 text-[10px] font-semibold uppercase tracking-[0.24em]"
                    style={{ color: st.accent }}
                  >
                    · {pick(st.title, lang)}
                  </div>
                </div>
              ))}
            </div>
          </Link>

          {/* Adım göstergeleri */}
          <div className="mt-6 flex items-center justify-center gap-2">
            {SUPPORT_STEPS.map((st, i) => (
              <span
                key={st.to}
                className="h-1.5 rounded-full transition-all duration-500"
                style={{
                  width: i === idx ? 26 : 9,
                  background: i === idx ? st.accent : 'rgba(255,255,255,0.22)',
                }}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

const SupportSticky = () => {
  const { t } = useTranslation();
  const lang = useLang();
  const trackRef = useRef<HTMLDivElement>(null);
  const [p, setP] = useState(0);
  useEffect(() => {
    let raf = 0;
    const onScroll = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        const el = trackRef.current;
        if (!el) return;
        const r = el.getBoundingClientRect();
        const span = r.height - window.innerHeight;
        setP(span > 0 ? clamp01(-r.top / span) : 0);
      });
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
    };
  }, []);

  const N = SUPPORT_STEPS.length;
  // Medya panelinin piksel ölçüsü — silüet buna göre üretilir.
  const panelRef = useRef<HTMLDivElement>(null);
  const [panelBox, setPanelBox] = useState({ w: 0, h: 0 });
  useEffect(() => {
    const el = panelRef.current;
    if (!el) return;
    const ro = new ResizeObserver((entries) => {
      const r = entries[0]?.contentRect;
      if (!r) return;
      setPanelBox((prev) =>
        Math.abs(prev.w - r.width) > 1 || Math.abs(prev.h - r.height) > 1
          ? { w: r.width, h: r.height }
          : prev,
      );
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  const idx = Math.min(N - 1, Math.floor(p * N));
  // Çentik konumu her adımda kayar: üst → orta → alt (döngüsel).
  const cardPath =
    panelBox.w > 0
      ? supportCardPath(
          panelBox.w,
          panelBox.h,
          NOTCH_SPOTS[idx % NOTCH_SPOTS.length],
        )
      : '';
  const title = t('home.supportPrograms');

  return (
    <section>
      <style>{`
        @keyframes ss-breathe { 0%,100% { opacity:.55; transform:scale(1); } 50% { opacity:1; transform:scale(1.06); } }
        .ss-breathe { animation: ss-breathe 9s ease-in-out infinite; }
        @media (prefers-reduced-motion: reduce) { .ss-breathe { animation: none; } }
      `}</style>

      {/* Masaüstü — pinli sticky-split sahne. Zemin: ÜSTTE belirgin (neon kenar
          çizgisinin altındaki saf siyah dolguyla birebir), AŞAĞI doğru
          şeffaflaşır; böylece arkadaki neon illüstrasyon alttan sızar.
          Kaydırma ilerledikçe üst kısım da tamamen şeffaflaşır. */}
      <div ref={trackRef} className="relative hidden h-[260vh] lg:block">
        <div
          className="sticky top-0 flex h-[100lvh] flex-col overflow-hidden"
          // Zemin YOK: sayfanın kendi (fixed) neon arka planı görünür. Bölüme
          // ayrı bir taban/kopya konmuyor — kopyanın kırpılan kenarı ince bir
          // dikiş çizgisi bırakıyordu.
        >
          {/* Bölüm başlığı */}
          <div className="relative pt-24 text-center">
            <h2 className="font-bricolage text-3xl font-bold tracking-tight text-white sm:text-4xl lg:text-5xl">
              {title}
            </h2>
          </div>

          <div className="relative mx-auto grid w-full max-w-7xl flex-1 grid-cols-2 items-center gap-14 px-8">
            {/* Sol — adım metinleri (çapraz geçiş) */}
            <div className="relative h-[20rem]">
              {SUPPORT_STEPS.map((st, i) => {
                const active = i === idx;
                // Adımın KENDİ içindeki ilerleme (0→1): yazma bu değerle akar.
                const local = clamp01(p * N - i);
                return (
                  <div
                    key={i}
                    className="absolute inset-0 flex flex-col justify-center"
                    style={{
                      opacity: active ? 1 : 0,
                      transform: active ? 'translateY(0)' : 'translateY(14px)',
                      transition: 'opacity .45s ease, transform .45s ease',
                      pointerEvents: active ? 'auto' : 'none',
                    }}
                  >
                    {/* Numara satırı ve alttaki "İncele" bağlantısı YOK;
                        yönlendirme sağ üstteki çapraz okta. */}
                    {/* pr-20: ok butonu 44px + boşluk; başlık uzun olduğunda
                        (ör. "Next Level / Sürdürülebilirlik Programı") yazı
                        okun altına giriyordu. */}
                    <div className="relative max-w-xl pr-20">
                      <Link
                        to={st.to}
                        aria-label={pick(st.title, lang)}
                        className="absolute right-0 top-0 flex h-10 w-10 lg:top-1 lg:h-11 lg:w-11 items-center justify-center rounded-full border border-white/15 text-white transition-all duration-300 hover:-translate-y-0.5 hover:translate-x-0.5"
                        style={{
                          color: st.accent,
                          borderColor: `${st.accent}55`,
                        }}
                      >
                        <ArrowRightOutlined className="-rotate-45 text-[14px]" />
                      </Link>
                      <h3 className="font-bricolage text-[30px] font-bold leading-[1.15] tracking-tight text-white">
                        <TypedText
                          text={pick(st.title, lang)}
                          t={(local - 0.05) / 0.35}
                        />
                      </h3>
                      <p
                        className="mt-6 text-[21px] leading-[1.5] text-slate-300"
                        aria-label={pick(st.desc, lang)}
                      >
                        <TypedText
                          text={pick(st.desc, lang)}
                          t={(local - 0.3) / 0.5}
                        />
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Sağ — yapışkan medya paneli: hedef sayfanın zemin dili */}
            <div
              ref={panelRef}
              // rounded-[34px]: clip-path ölçülene KADAR (ilk boyama, ölçüm
              // gecikmesi) kart keskin köşeli bir dikdörtgen gibi görünüyordu;
              // yedek yuvarlaklık her koşulda duruyor.
              className="relative aspect-[4/5] max-h-[70vh] w-full justify-self-end overflow-hidden rounded-[34px]"
              style={{
                clipPath: cardPath ? `path('${cardPath}')` : undefined,
                transition: 'clip-path 600ms cubic-bezier(.22,.8,.24,1)',
              }}
            >
              {SUPPORT_STEPS.map((st, i) => {
                const active = i === idx;
                return (
                  <div
                    key={i}
                    className="absolute inset-0"
                    style={{
                      opacity: active ? 1 : 0,
                      transition: 'opacity .9s ease',
                      background: st.panelBg,
                    }}
                  >
                    {/* Hedef sayfanın ince ızgarası — kenarlarda erir */}
                    <div
                      aria-hidden
                      className="absolute inset-0"
                      style={{
                        backgroundImage: `linear-gradient(${st.grid} 1px, transparent 1px), linear-gradient(90deg, ${st.grid} 1px, transparent 1px)`,
                        backgroundSize: '56px 56px',
                        maskImage:
                          'radial-gradient(80% 70% at 50% 38%, #000 25%, transparent 100%)',
                        WebkitMaskImage:
                          'radial-gradient(80% 70% at 50% 38%, #000 25%, transparent 100%)',
                      }}
                    />
                    {/* Nefes alan accent parıltıları */}
                    <div
                      aria-hidden
                      className="ss-breathe absolute left-1/2 top-[6%] h-[46%] w-[80%] -translate-x-1/2 rounded-full"
                      style={{
                        background: `radial-gradient(closest-side, ${st.accent}2b, transparent)`,
                        filter: 'blur(28px)',
                      }}
                    />
                    <div
                      aria-hidden
                      className="ss-breathe absolute bottom-[-8%] right-[-12%] h-[38%] w-[60%] rounded-full"
                      style={{
                        background: `radial-gradient(closest-side, ${st.accent}1c, transparent)`,
                        filter: 'blur(32px)',
                        animationDelay: '2.2s',
                      }}
                    />
                    {/* Program logosu */}
                    <div className="absolute inset-0 flex items-center justify-center">
                      <img
                        src={st.logo}
                        alt={pick(st.title, lang)}
                        className="h-24 w-auto max-w-[62%] object-contain"
                        style={{
                          filter: `drop-shadow(0 0 30px ${st.accent}88)`,
                        }}
                      />
                    </div>
                    {/* Alt köşede mono rozet */}
                    <div
                      className="absolute bottom-6 left-6 text-[11px] font-semibold uppercase tracking-[0.24em]"
                      style={{ color: st.accent }}
                    >
                      · {pick(st.title, lang)}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      {/* Mobil — masaüstündeki animasyonlu kurgunun dikey hâli */}
      <SupportMobileScene title={title} lang={lang} />
    </section>
  );
};

// ── KAPSÜL GELİŞİM PROGRAMLARI — pinli sahne ─────────────────────────────────
// ORTADA grup adı (Yetenek → Yetkinlik) DESTEK bölümündeki daktilo geçişiyle
// yazılır/silinir; SOLDA ve SAĞDA o grubun kartları belirir. Kartların dili
// destek bölümündekinin AYNISI: hedef programın accent'iyle boyanmış zemin +
// ince ızgara + nefes alan parıltı + ortada logo + başlık/açıklama/İncele.
// key: i18n anahtarı (home.groupTalent / home.groupCompetency)
const DEV_GROUPS: { key: string; items: number[] }[] = [
  { key: 'home.groupTalent', items: [0, 1] },
  { key: 'home.groupCompetency', items: [2, 3] },
];

// Kart zemini/ızgarası — destek panelleriyle aynı dil, programın accent'inden.
const devPanelBg = (accent: string) =>
  `radial-gradient(120% 90% at 50% -10%, ${accent}40 0%, #0b0e18 46%, #05070c 100%)`;

// Kart gövdesi — panelin İÇERİĞİ (zemin + ızgara + parıltı + logo + metin).
// Grup değişince kartlar YOK OLMAZ; yalnızca bu katman çapraz geçer.
const DevCardFace = ({
  card,
  show,
  lang,
}: {
  card: (typeof DEV_BOX_CARDS)[number];
  show: number;
  lang: 'tr' | 'en';
}) => (
  <div
    className="absolute inset-0"
    style={{
      background: devPanelBg(card.accent),
      opacity: show,
      transition: 'opacity .5s ease',
    }}
  >
    {/* Hedef sayfanın ince ızgarası — kenarlarda erir */}
    <div
      aria-hidden
      className="absolute inset-0"
      style={{
        backgroundImage: `linear-gradient(${card.accent}14 1px, transparent 1px), linear-gradient(90deg, ${card.accent}14 1px, transparent 1px)`,
        backgroundSize: '52px 52px',
        maskImage:
          'radial-gradient(85% 75% at 50% 32%, #000 25%, transparent 100%)',
        WebkitMaskImage:
          'radial-gradient(85% 75% at 50% 32%, #000 25%, transparent 100%)',
      }}
    />
    {/* Nefes alan accent parıltıları — destek panelindeki dil */}
    <div
      aria-hidden
      className="ss-breathe absolute left-1/2 top-[2%] h-[46%] w-[80%] -translate-x-1/2 rounded-full"
      style={{
        background: `radial-gradient(closest-side, ${card.accent}33, transparent)`,
        filter: 'blur(28px)',
      }}
    />
    <div
      aria-hidden
      className="ss-breathe absolute bottom-[-8%] right-[-12%] h-[38%] w-[60%] rounded-full"
      style={{
        background: `radial-gradient(closest-side, ${card.accent}1c, transparent)`,
        filter: 'blur(32px)',
        animationDelay: '2.2s',
      }}
    />

    <div className="relative flex h-full flex-col p-5 sm:p-6 lg:p-8">
      {/* Logo — panelin ortasında, accent ışımalı. Dikey lockup'ı olan
          program masaüstündeki uzun kartta onu, mobildeki kısa kartta yatayı
          basar (48px'lik dikey logo okunmuyordu). */}
      <div className="flex flex-1 items-center justify-center py-4">
        <img
          src={card.logo}
          alt=""
          className={`h-12 w-auto max-w-[62%] object-contain ${
            card.logoVertical ? 'lg:hidden' : 'lg:h-20'
          }`}
          style={{ filter: `drop-shadow(0 0 30px ${card.accent}88)` }}
        />
        {card.logoVertical && (
          <img
            src={card.logoVertical}
            alt=""
            className="hidden h-40 w-auto max-w-[62%] object-contain lg:block"
            style={{ filter: `drop-shadow(0 0 30px ${card.accent}88)` }}
          />
        )}
      </div>
      {/* Başlık + açıklama; sağ üstte destek bölümündeki yuvarlak ok butonu */}
      <div className="relative pr-14 lg:pr-16">
        <span
          aria-hidden
          className="absolute right-0 top-1 flex h-11 w-11 items-center justify-center rounded-full border transition-all duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5"
          style={{ color: card.accent, borderColor: `${card.accent}55` }}
        >
          <ArrowRightOutlined className="-rotate-45 text-[14px]" />
        </span>
        <h3 className="font-bricolage text-lg font-bold leading-tight text-white lg:text-2xl">
          {pick(card.title, lang)}
        </h3>
        <p className="mt-2 line-clamp-3 text-[13px] leading-relaxed text-slate-300/80 lg:mt-3 lg:text-sm">
          {pick(card.desc, lang)}
        </p>
      </div>
    </div>
  </div>
);

// Yan panel — sahne boyunca EKRANDA KALIR. Grup değiştikçe:
//   • çentik yukarıdan aşağıya KAYAR (Yetenek üstte → Yetkinlik altta),
//   • içerik (logo/başlık/açıklama) çapraz geçişle değişir.
const DevCard = ({
  cards,
  active,
  lang,
  reveal,
  dir,
  spot,
  side = 'left',
  slide = true,
}: {
  /** Bu tarafın grup başına kartları (index = grup sırası) */
  cards: (typeof DEV_BOX_CARDS)[number][];
  active: number;
  lang: 'tr' | 'en';
  reveal: number;
  dir: -1 | 1;
  /** Çentiğin dikey konumu (0→1) — scroll ile sürekli kayar */
  spot: number;
  /** Çentiğin hangi kenarda olduğu — sahnede hep İÇE (merkeze) bakar */
  side?: 'left' | 'right' | 'top' | 'bottom';
  /** Girişte yatay süzülme (mobilde kapalı: sayfayı yatay taşırıyordu) */
  slide?: boolean;
}) => {
  const { t } = useTranslation();
  const ref = useRef<HTMLAnchorElement>(null);
  const [box, setBox] = useState({ w: 0, h: 0 });
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const ro = new ResizeObserver((entries) => {
      const r = entries[0]?.contentRect;
      if (!r) return;
      setBox((prev) =>
        Math.abs(prev.w - r.width) > 1 || Math.abs(prev.h - r.height) > 1
          ? { w: r.width, h: r.height }
          : prev,
      );
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  // Destek panelindeki çentikli gövde. Çentik varsayılan olarak SOL kenarda;
  // mirror ile SAĞ kenara alınır (dış kutu -1 ölçekle çevrilir, içerik ikinci
  // kez çevrilerek düzelir). Sahnede çentikler İÇE, merkeze bakar.
  const mirror = side === 'right';
  const path =
    box.w === 0
      ? ''
      : side === 'top' || side === 'bottom'
        ? cardNotchPathV(box.w, box.h, spot, side)
        : supportCardPath(box.w, box.h, spot);
  const card = cards[active];

  return (
    <Link
      ref={ref}
      to={card.to}
      aria-label={pick(card.title, lang)}
      // Yüzler mutlak konumlu → yükseklik kartın kendisinden gelmeli.
      // Yükseklik ölçüsü ekranın kendisine bağlanır: kısa ekranlarda (alçak
      // laptop, yatay tablet) kart sahneye sığmayıp başlığın üzerine biniyordu.
      // Masaüstünde GENİŞLİK yükseklikten türetilir (46lvh × 5/4 ≈ 58lvh) →
      // kart daralır ama 4/5 oranını korur, max-h ile ezilip yayvanlaşmaz.
      className="group relative block h-[26dvh] min-h-[min(10.5rem,22dvh)] overflow-hidden rounded-[34px] transition-transform duration-300 hover:-translate-y-1 lg:aspect-[4/5] lg:h-auto lg:max-h-[58lvh] lg:w-[min(100%,46lvh)] lg:justify-self-center"
      style={{
        opacity: reveal,
        transform: `translateX(${slide ? dir * (1 - reveal) * 46 : 0}px) scaleX(${mirror ? -1 : 1})`,
        clipPath: path ? `path('${path}')` : undefined,
      }}
    >
      <div
        className="h-full"
        style={{ transform: `scaleX(${mirror ? -1 : 1})` }}
      >
        {cards.map((c, i) => (
          <DevCardFace
            key={c.to}
            card={c}
            show={i === active ? 1 : 0}
            lang={lang}
          />
        ))}
        <span className="sr-only">{t('home.explore')}</span>
      </div>
    </Link>
  );
};

const DevelopmentStage = () => {
  const { t } = useTranslation();
  const lang = useLang();
  const trackRef = useRef<HTMLDivElement>(null);
  const mobRef = useRef<HTMLDivElement>(null);
  const [p, setP] = useState(0);
  const [mp, setMp] = useState(0); // mobil parkurun kendi ilerlemesi

  useEffect(() => {
    let raf = 0;
    const measure = (el: HTMLDivElement | null) => {
      if (!el) return 0;
      const r = el.getBoundingClientRect();
      const dist = r.height - window.innerHeight;
      return dist > 0 ? clamp01(-r.top / dist) : 0;
    };
    const onScroll = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        setP(measure(trackRef.current));
        setMp(measure(mobRef.current));
      });
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
    };
  }, []);

  const G = DEV_GROUPS.length;
  // Parkurun İLK %14'ü başlığa ayrılır: "Kapsül Gelişim Programları" ekran
  // ORTASINDA büyük belirir, üstteki yuvasına inip küçülür (diğer bölümlerle
  // aynı yerleşme efekti). Gruplar ancak bundan sonra akmaya başlar.
  const settle = easeOut3(clamp01(p / 0.12));
  const sceneIn = easeOut3(clamp01((p - 0.12) / 0.08));
  // Sahne YERLEŞENE kadar (ve sonunda kısa bir süre) grup akışı BEKLER:
  // kartlar tam görünür olmadan çentik kaymaz, sonda da aceleyle bitmez.
  const q = clamp01((p - 0.26) / 0.74);
  const step = q * G;
  const idx = Math.min(G - 1, Math.floor(step));
  const local = clamp01(step - idx);
  const last = idx === G - 1;

  // Grup adı yazılır (0→0.30), grup biterken silinir (0.80→1).
  const write = clamp01(local / 0.22);
  const erase = last ? 0 : clamp01((local - 0.86) / 0.14);
  // Kartlar sahnede KALIR: yalnızca ilk açılışta içeri süzülür.
  const cardReveal = sceneIn;
  // Çentik, parkur boyunca yukarıdan aşağıya kayar: Yetenek'te ÜSTTE,
  // Yetkinlik'e geçilirken aşağı iner (kart yok olmaz, şekil dönüşür).
  // Yumuşak (smoothstep) kayma — başta ve sonda sakin, ortada akıcı.
  const notchSpot = 0.28 + q * q * (3 - 2 * q) * 0.44;

  // ── Mobil sahne — aynı akış, kendi parkuru üzerinden ──
  const mSettle = easeOut3(clamp01(mp / 0.12));
  const mSceneIn = easeOut3(clamp01((mp - 0.12) / 0.08));
  const mq = clamp01((mp - 0.22) / 0.78);
  const mStep = mq * G;
  const mIdx = Math.min(G - 1, Math.floor(mStep));
  const mLocal = clamp01(mStep - mIdx);
  const mWrite = clamp01(mLocal / 0.22);
  const mErase = mIdx === G - 1 ? 0 : clamp01((mLocal - 0.86) / 0.14);
  // Çentik dikeyde kayar; alt kartta ters yönde (1 - mNotch) → iki kart
  // birbirine bakan bir zikzak çizer.
  const mNotch = 0.28 + mq * mq * (3 - 2 * mq) * 0.44;
  const mGroup = DEV_GROUPS[mIdx];

  const group = DEV_GROUPS[idx];
  const label = t(group.key);
  const title = t('home.developmentPrograms');

  return (
    <>
      {/* Masaüstü — pinli sahne */}
      <div
        ref={trackRef}
        className="relative hidden lg:block"
        style={{ height: `${G * 210 + 90}vh` }}
      >
        <div className="sticky top-0 flex h-[100lvh] flex-col overflow-hidden">
          {/* Başlık — büyükten üst yuvasına oturur. Yuva AKIŞIN İÇİNDE: başlık
              absolute (top-[15vh]) iken kartlar da ekranın tamamına göre
              ortalandığından iki blok birbirinden habersizdi; yüksekliği düşük
              ekranlarda üst üste biniyorlardı. */}
          <div className="pointer-events-none relative z-10 shrink-0 px-4 pt-[13lvh] text-center">
            <h2
              className="font-bricolage text-3xl font-bold tracking-tight text-white sm:text-4xl lg:text-5xl"
              style={{
                transform: `translateY(${(1 - settle) * 34}vh) scale(${1 + 0.65 * (1 - settle)})`,
                transformOrigin: 'center',
                textShadow: `0 0 ${40 * (1 - settle)}px ${ACCENT}44`,
              }}
            >
              {title}
            </h2>
          </div>

          {/* Sahne, başlıktan ARTAN alanda dikeyde ortalanır (min-h-0: flex
              çocuğu içeriğinden küçülebilsin, taşma yerine sığsın). */}
          <div className="flex min-h-0 flex-1 items-center pb-[5lvh]">
            <div
              className="mx-auto grid w-full max-w-7xl grid-cols-[1fr_auto_1fr] items-center gap-10 px-8 xl:gap-14"
              style={{ opacity: sceneIn }}
            >
              <DevCard
                cards={DEV_GROUPS.map((g) => DEV_BOX_CARDS[g.items[0]])}
                active={idx}
                lang={lang}
                reveal={cardReveal}
                dir={-1}
                spot={notchSpot}
                side="right"
              />

              {/* Merkez — grup adı, destek bölümündeki daktilo geçişiyle */}
              <div
                className="w-[15rem] text-center xl:w-[18rem]"
                style={{ opacity: 1 - erase }}
              >
                <div
                  className="text-[11px] font-semibold uppercase tracking-[0.28em]"
                  style={{ color: `${ACCENT}cc`, opacity: clamp01(write * 3) }}
                >
                  {`0${idx + 1} / 0${G}`}
                </div>
                <div className="mt-4 font-bricolage text-4xl font-bold leading-[1.1] tracking-tight text-white xl:text-5xl">
                  <TypedText text={label} t={write} />
                </div>
                <div
                  aria-hidden
                  className="mx-auto mt-6 h-px w-24 origin-center"
                  style={{
                    background: `linear-gradient(90deg, transparent, ${ACCENT}, transparent)`,
                    transform: `scaleX(${clamp01(write * 1.4)})`,
                  }}
                />
                <div className="mt-8 flex items-center justify-center gap-2">
                  {DEV_GROUPS.map((g, i) => (
                    <span
                      key={g.key}
                      className="h-1.5 rounded-full transition-all duration-500"
                      style={{
                        width: i === idx ? 28 : 10,
                        background:
                          i === idx ? ACCENT : 'rgba(255,255,255,0.22)',
                      }}
                    />
                  ))}
                </div>
              </div>

              <DevCard
                cards={DEV_GROUPS.map((g) => DEV_BOX_CARDS[g.items[1]])}
                active={idx}
                lang={lang}
                reveal={cardReveal}
                dir={1}
                spot={notchSpot}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Mobil/tablet — aynı sahne DİKEY kurulur: kartlardan biri ÜSTTE biri
          ALTTA, çentikler içe (üstteki sağda, alttaki solda) bakar; kaydırma
          ilerledikçe çentik soldan sağa/aşağı kayar ve Yetenek → Yetkinlik
          geçişi olur. */}
      <div
        ref={mobRef}
        className="relative overflow-x-clip lg:hidden"
        style={{ height: `${G * 130 + 50}vh` }}
      >
        <div className="sticky top-0 flex h-[100lvh] flex-col justify-center overflow-hidden px-5">
          {/* Başlık akışın İÇİNDE: kartlarla tek blok hâlinde dikeyde
              ortalanır; absolute konumdayken altta ölü boşluk kalıyordu. */}
          <h2
            className="mx-auto mb-6 max-w-[22rem] text-center font-bricolage text-2xl font-bold leading-tight tracking-tight text-white"
            style={{ opacity: mSettle }}
          >
            {title}
          </h2>

          <div
            className="mx-auto flex w-full max-w-md flex-col items-stretch gap-4"
            style={{ opacity: mSceneIn }}
          >
            {/* ÜST kart — çentik SAĞDA (içe bakar) */}
            <DevCard
              cards={DEV_GROUPS.map((g) => DEV_BOX_CARDS[g.items[0]])}
              active={mIdx}
              lang={lang}
              reveal={mSceneIn}
              dir={-1}
              spot={mNotch}
              side="bottom"
              slide={false}
            />

            {/* Orta — grup adı, daktilo geçişiyle */}
            <div className="py-1 text-center" style={{ opacity: 1 - mErase }}>
              <div className="font-bricolage text-2xl font-bold tracking-tight text-white">
                <TypedText text={t(mGroup.key)} t={mWrite} />
              </div>
              <div className="mt-3 flex items-center justify-center gap-2">
                {DEV_GROUPS.map((g, i) => (
                  <span
                    key={g.key}
                    className="h-1.5 rounded-full transition-all duration-500"
                    style={{
                      width: i === mIdx ? 24 : 9,
                      background:
                        i === mIdx ? ACCENT : 'rgba(255,255,255,0.22)',
                    }}
                  />
                ))}
              </div>
            </div>

            {/* ALT kart — çentik SOLDA (içe bakar) */}
            <DevCard
              cards={DEV_GROUPS.map((g) => DEV_BOX_CARDS[g.items[1]])}
              active={mIdx}
              lang={lang}
              reveal={mSceneIn}
              dir={1}
              spot={1 - mNotch}
              side="top"
              slide={false}
            />
          </div>
        </div>
      </div>
    </>
  );
};

// Ana sayfa — BAŞTAN AŞAĞI Terminal program sayfası tasarım dili: sabit
// çizgisel Kapsül "K" desenli koyu zemin, "yoktan var olan" hero, scroll ile
// beliren tam genişlik bölümler (Destek → Gelişim → Etkinlik → Basın →
// Paydaşlar). Scrolljacking yok; doğal akış.
const Home = () => {
  const { t } = useTranslation();
  const lang = useLang();
  usePageTitle(undefined, t('home.heroSubtitle'));
  const compactHome = useCompactHomeMode();
  const [events, setEvents] = useState<EventItem[] | null>(null);
  const [press, setPress] = useState<PressItem[] | null>(null);

  useEffect(() => {
    let active = true;
    let idle = 0;

    const loadSecondaryContent = () => {
      if (!active) return;
      listEvents()
        .then((r) => active && setEvents(r.records))
        .catch(() => active && setEvents([]));
      listPress()
        .then((r) => active && setPress(r.records))
        .catch(() => active && setPress([]));
    };

    const schedule = () => {
      const ric = (
        window as Window &
          typeof globalThis & {
            requestIdleCallback?: (
              cb: () => void,
              options?: { timeout: number },
            ) => number;
          }
      ).requestIdleCallback;
      idle = ric
        ? ric(loadSecondaryContent, { timeout: 3000 })
        : window.setTimeout(loadSecondaryContent, 1600);
    };

    if (document.readyState === 'complete') schedule();
    else window.addEventListener('load', schedule, { once: true });

    return () => {
      active = false;
      window.removeEventListener('load', schedule);
      window.clearTimeout(idle);
    };
  }, []);

  const eventItems =
    events === null
      ? Array.from({ length: 3 }, (_, i) => <MediaPlaceholderCard key={i} />)
      : events.slice(0, 3).map((e) => (
          <ContentCard
            key={e._id}
            overlay
            // Kaydın kendi bağlantısı varsa oraya (yeni sekme), yoksa
            // site içi detay sayfasına gider.
            href={e.link || undefined}
            to={`/events/${e._id}`}
            image={driveImageUrl(e.images?.[0])}
            title={pick(e.title, lang)}
            date={formatDate(e.date, lang)}
            excerpt={pick(e.description, lang)}
          />
        ));

  const pressItems =
    press === null
      ? Array.from({ length: 3 }, (_, i) => <MediaPlaceholderCard key={i} />)
      : press
          .slice(0, 3)
          .map((pr) => (
            <ContentCard
              key={pr._id}
              overlay
              href={pr.links?.[0] || undefined}
              to={`/press/${pr._id}`}
              image={driveImageUrl(pr.images?.[0])}
              title={pick(pr.title, lang)}
              date={formatDate(pr.date, lang)}
              excerpt={pick(pr.description, lang)}
            />
          ));

  return (
    // overflow-x-clip: bölümlerdeki taşan dekoratif katmanlar (parıltı,
    // süzülme transformları) mobilde sayfayı yatay kaydırılabilir yapıp tüm
    // bölümleri kaymış gösteriyordu.
    <div className="relative overflow-x-clip bg-[#03060f] text-white">
      {/* Sabit zemin — kapsul-home.svg illüstrasyonu (azure tonda, çok silik);
          scroll ilerledikçe çizgiler AŞAĞIDAN YUKARIYA doğru kademeli söner. */}
      <HomeBackdrop />

      <div className="relative z-10">
        {/* ── FRAME AÇILIŞI — scroll'la akan kare dizisi (tam ekran, navbar'ın
            altına full-bleed girer). Patlama anında tanıtım yazısı yazılır. ── */}
        <div
          className="-mt-20"
          data-laser-frame-hero
          data-scroll-frames
          {...(compactHome ? { 'data-home-frame-ready': 'true' } : {})}
        >
          {compactHome ? <MobileHomeHero /> : <LaserFrameHero />}
        </div>
      </div>

      {/* Sonraki içerik pinli frame'in ÜZERİNE alttan kayarak (sheet gibi)
          BİNER: 100lvh bindirme, üst kenarında neon kapsül çizgisi. Frame
          bölümü 460vh + FRAME_END 0.66 tam bu bindirme için ölçülüdür —
          kareler 238vh'de biter, kayma 260vh'de başlar (bkz. LaserFrameHero).
          Frame kaybolmaz; sheet onun üzerini örterek gelir.
          Bindirme lvh ile verilir: sticky yüzey de h-[100lvh] olduğundan mobil
          tarayıcıda adres çubuğu açılıp kapanınca ikisi ayrışmaz. */}
      <div
        className={`relative z-20 ${compactHome ? '' : '-mt-[100lvh]'}`}
        // Sheet ÜSTTEN kare dizisinin saf siyahıyla başlar ve sayfanın sabit
        // lacivert degradesine erir; kenar çizgisinin altında ton farkı /
        // dikiş bandı kalmaz. Zemin olduğu için içerik üstünde durur.
        // İLK 100lvh TAM OPAK: bindirme boyunca arkada hâlâ pinli duran kare
        // dizisi buradan sızmasın (bindirmenin eskiden kaldırılma sebebi buydu).
        // Bindirme bitip frame geçildikten sonra zemin şeffaflaşır ve arkadaki
        // neon çizim görünür olur.
        style={
          compactHome
            ? undefined
            : {
                backgroundImage:
                  'linear-gradient(to bottom, #000 0px, #000 100lvh, rgba(0,0,0,0) 175lvh)',
                backgroundRepeat: 'no-repeat',
              }
        }
      >
        {/* Neon kenar tutucusu — çizgi bu noktanın üzerinde (sheet üst kenarı) */}
        {!compactHome && (
          <div className="relative">
            <SheetNeonEdge />
          </div>
        )}

        {/* ── KAPSÜL DESTEK PROGRAMLARI — sticky-split: solda numaralı adım
            metinleri, sağda hedef sayfanın zemin dilinde yapışkan panel ── */}
        <SupportSticky />

        {/* ── KAPSÜL GELİŞİM PROGRAMLARI — pinli sahne: ortada Yetenek →
            Yetkinlik daktilo geçişi, iki yanda destek dilinde kartlar ── */}
        <DevelopmentStage />

        {/* ── SON ETKİNLİKLER — aynı pinli sahne ── */}
        {(events === null || events.length > 0) && (
          <StageSection
            title={t('home.latestEvents')}
            flat
            mobileCarousel
            gridClass="max-w-6xl sm:grid-cols-2 lg:grid-cols-3"
            items={eventItems}
            footer={<SeeAll to="/events" label={t('common.seeAll')} />}
          />
        )}

        {/* ── BASINDA BİZ — aynı pinli sahne ── */}
        {(press === null || press.length > 0) && (
          <StageSection
            title={t('home.latestPress')}
            flat
            mobileCarousel
            gridClass="max-w-6xl sm:grid-cols-2 lg:grid-cols-3"
            items={pressItems}
            footer={<SeeAll to="/press" label={t('common.seeAll')} />}
          />
        )}

        {/* ── PAYDAŞLAR — ana sayfa akışı: başlık ortada büyük belirir,
            üst yuvasına oturur, paydaş sahnesi açılır; sahnenin sonunda
            footer paydaşların üzerinden kapanır ── */}
        <StakeholdersStage />
      </div>
    </div>
  );
};

export default Home;
